import { listConcepts } from './../graphql/queries';
import { useDispatch, useSelector } from 'react-redux';
import {
  setBranches,
  setListing,
  setSelected,
  setFilters,
  setSelectedFilters,
  setNextToken,
} from '../store/ducks/concept';
import { Concept } from '../models/GQL_API';
import { HeadCell } from '../models/dataTable';
import useApp from './useApp';
import { CreateConceptInput, UpdateConceptInput } from '../models/GQL_API';
import {
  ConceptBulkTrashVariables,
  CreateVariables,
  ConceptGetVariables,
  ConceptListingVariables,
  Option,
} from '../models/app';
import { getConcept } from '../graphql/queries';
import { AppTypes, LOCAL_STORAGE, appType } from '../constants/enums';
import {
  createConcept,
  deleteConcept,
  updateConcept,
} from '../graphql/mutations';
import { onCreateConcept } from '../graphql/subscriptions';

import { generateClient } from '@aws-amplify/api';
import {
  clearLocalStorageData,
  sessionTimeout,
  invokeLambda,
} from '../helpers/utils';

const client = generateClient();

// List of credential fields that need encryption
const CREDENTIAL_FIELDS = [
  'ohipClientId',
  'ohipClientSecret',
  'ohipAuthUrl',
  'ohipApiBaseUrl',
  'ohipAppKey',
  'ohipEnterpriseId',
  'ohipExternalSystem',
  'ohipScope',
  'ohipUsername',
  'ohipPassword',
  'ohipGrantType',
  'whatsappToken',
  'whatsappApiUrl',
];

/**
 * Encrypt credential fields using Lambda function
 */
async function encryptCredentialFields(obj: any): Promise<any> {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  try {
    // Check if any credential fields need encryption
    const hasCredentialFields = CREDENTIAL_FIELDS.some(
      (field) =>
        obj[field] !== undefined && obj[field] !== null && obj[field] !== '',
    );

    if (!hasCredentialFields) {
      return obj;
    }

    // Call Lambda function to encrypt
    // The Lambda expects an AppSync resolver event structure
    const event = {
      info: {
        fieldName: 'createConcept', // Will be 'createConcept' or 'updateConcept'
      },
      arguments: {
        input: obj,
      },
    };

    // invokeLambda wraps the body, so we need to pass the event as the body
    const result = await invokeLambda(
      'conceptIntegrationCredentialsManager',
      event,
    );

    // The Lambda handler processes the event and returns it with encrypted fields
    // Since invokeLambda wraps it, we need to extract the result properly
    // The Lambda returns the event structure, so we need to get the encrypted input
    if (result && result.arguments && result.arguments.input) {
      return result.arguments.input;
    }

    // Fallback: if Lambda call fails or returns unexpected format, return original
    return obj;
  } catch (error) {
    console.error('Error encrypting credential fields:', error);
    // If encryption fails, return original object (don't break the update)
    return obj;
  }
}

/**
 * Decrypt credential fields using Lambda function
 */
async function decryptCredentialFields(obj: any): Promise<any> {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  try {
    // Check if any credential fields need decryption
    const hasCredentialFields = CREDENTIAL_FIELDS.some(
      (field) =>
        obj[field] !== undefined && obj[field] !== null && obj[field] !== '',
    );

    if (!hasCredentialFields) {
      return obj;
    }

    // Call Lambda function to decrypt
    // The Lambda expects an AppSync resolver event structure
    const event = {
      prev: {
        result: obj,
      },
    };

    // invokeLambda wraps the body, so we need to pass the event as the body
    const result = await invokeLambda(
      'conceptIntegrationCredentialsManager',
      event,
    );

    // The Lambda handler processes the event and returns it with decrypted fields
    // Since invokeLambda wraps it, we need to extract the result properly
    if (result && result.prev && result.prev.result) {
      return result.prev.result;
    }

    // Fallback: if Lambda call fails or returns unexpected format, return original
    return obj;
  } catch (error) {
    console.error('Error decrypting credential fields:', error);
    // If decryption fails, return original object
    return obj;
  }
}

const useResource = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const { showConfirm, showError } = useApp();
  const listing = useSelector(
    (state: any) => state[`${listingName}`]['listing'],
  );
  const nextToken = useSelector(
    (state: any) => state[`${listingName}`]['nextToken'],
  );

  async function fetch(params: ConceptListingVariables, isAuth = true) {
    const { accountID } = params;

    try {
      const offlineUserConcept = localStorage.getItem('offlineUserConcept');

      if (
        offlineUserConcept &&
        !sessionTimeout() &&
        // @ts-ignore
        appType === AppTypes.CASHLESS
      ) {
        let userConcept = JSON.parse(offlineUserConcept);
        if (userConcept) {
          dispatch(setSelected(userConcept));
          return userConcept;
        }
      } else {
        clearLocalStorageData();
        const filter: any = {
          deleted: { eq: '0' },
          accountID: { eq: accountID },
        };

        const conceptListing: any = await client.graphql({
          query: listConcepts,
          variables: {
            filter,
            limit: 100000,
          },
          authMode: isAuth ? 'userPool' : 'iam',
        });

        const currentNextToken = conceptListing.data.listConcepts.nextToken;
        let listing: any = conceptListing.data.listConcepts.items;

        // Decrypt credential fields after fetching
        listing = await Promise.all(
          listing.map((item: any) => decryptCredentialFields(item)),
        );

        localStorage.setItem('offlineUserConcept', JSON.stringify(listing));

        dispatch(setNextToken(currentNextToken));

        return listing;
      }
    } catch (err: Error | any) {
      console.log(err);
      showError(err.message || err);
      return [];
    }
  }

  async function fetchAllOnline() {
    const filter: any = {
      deleted: { eq: '0' },
    };

    try {
      const listing: any = await client.graphql({
        query: listConcepts,
        variables: { filter, limit: 100000 },
        authMode: 'userPool',
      });

      let items = listing.data.listConcepts.items;

      // Decrypt credential fields after fetching
      items = await Promise.all(
        items.map((item: any) => decryptCredentialFields(item)),
      );

      return items;
    } catch (err: Error | any) {
      console.log(err);
      showError(err.message || err);
      return [];
    }
  }

  async function get(params: ConceptGetVariables) {
    const { id, listing } = params;

    try {
      let single: Concept | undefined;
      if (listing.length > 0) {
        single = listing.find((resource: any) => resource.id === id);
      } else {
        const listing: any = await client.graphql<Concept>({
          query: getConcept,
          variables: { id },
          authMode: true ? 'userPool' : 'iam',
        });
        single = listing.data.getConcept;

        // Decrypt credential fields after fetching
        if (single) {
          single = await decryptCredentialFields(single);
        }
      }

      return single;
    } catch (err) {
      showError(err);
    }
  }

  async function getOnline(id: string, isAuth: boolean) {
    try {
      const user: any = await client.graphql({
        query: getConcept,
        variables: { id: id },
        authMode: isAuth ? 'userPool' : 'iam',
      });

      let concept = user.data.getConcept;

      // Decrypt credential fields after fetching
      if (concept) {
        concept = await decryptCredentialFields(concept);
      }

      return concept;
    } catch (err) {
      showError(err);
    }
  }

  async function create(params: CreateVariables) {
    const { userID, userName, data } = params;
    if (!data.accountID) {
      const error = new Error(`Cannot create ${singleName} without accountID`);
      return showError(error);
    }

    try {
      const createInput: any = {
        accountID: data.accountID,
        name: data.name,
        description: data.description ? data.description : '',
        location: data.location ? data.location : '',
        type: data.type ? data.type : '',
        logo: data.logo ? data.logo : '',

        // Concept Contacts
        contactWhatsApp: data.contactWhatsApp || '',
        contactPhoneNumber: data.contactPhoneNumber || '',
        contactEmail: data.contactEmail || '',

        // OHIP Integration Credentials (will be encrypted before sending)
        ohipClientId: data.ohipClientId || null,
        ohipClientSecret: data.ohipClientSecret || null,
        ohipAuthUrl: data.ohipAuthUrl || null,
        ohipApiBaseUrl: data.ohipApiBaseUrl || null,
        ohipHotelId: data.ohipHotelId || null,
        ohipAppKey: data.ohipAppKey || null,
        ohipEnterpriseId: data.ohipEnterpriseId || null,
        ohipExternalSystem: data.ohipExternalSystem || null,
        ohipScope: data.ohipScope || null,
        ohipUsername: data.ohipUsername || null,
        ohipPassword: data.ohipPassword || null,
        ohipGrantType: data.ohipGrantType || null,

        // Email Integration Credentials
        senderEmail: data.senderEmail || null,

        // WhatsApp Integration Credentials
        whatsappToken: data.whatsappToken || null,
        whatsappApiUrl: data.whatsappApiUrl || null,
        whatsappPhoneId: data.whatsappPhoneId || null,
        deleted: '0',
        createdAt: new Date().toISOString(),
        createdByID: userID,
        createdByName: userName,
      };

      // Encrypt credential fields before sending to GraphQL
      const encryptedInput = await encryptCredentialFields(createInput);

      await client.graphql<Concept>({
        query: createConcept,
        variables: { input: encryptedInput },
        authMode: 'userPool',
      });

      showConfirm(`New ${singleName} has been created successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function update(params: any) {
    const { id, listing, data, newReceiveWaiter, delteWaiterID } = params;

    try {
      const original: any = await get({ id, listing });

      if (!original) {
        showError(`Invalid ${singleName} ID`);
        return;
      }

      if (!newReceiveWaiter && !delteWaiterID) {
        const updateInput: any = {
          id: original.id,
          name: data.name ? data.name : original!.name,
          precedence: data.precedence
            ? parseInt(data.precedence)
            : original.precedence,
          description:
            data.description !== undefined
              ? data.description
              : original.description,
          location:
            data.location !== undefined ? data.location : original.location,
          type: data.type !== undefined ? data.type : original.type,
          logo: data.logo !== undefined ? data.logo : original.logo,
          longitude:
            data.longitude !== undefined ? data.longitude : original.longitude,
          latitude:
            data.latitude !== undefined ? data.latitude : original.latitude,

          // Concept Contacts
          contactWhatsApp:
            data.contactWhatsApp !== undefined
              ? data.contactWhatsApp
              : original.contactWhatsApp,
          contactPhoneNumber:
            data.contactPhoneNumber !== undefined
              ? data.contactPhoneNumber
              : original.contactPhoneNumber,
          contactEmail:
            data.contactEmail !== undefined
              ? data.contactEmail
              : original.contactEmail,

          // WhatsApp Auto-Reply Toggle + Message
          autoReplyEnabled: !!data.autoReplyEnabled,
          autoReplyText:
            data.autoReplyText !== undefined
              ? data.autoReplyText
              : original.autoReplyText,

          // OHIP Integration Credentials (will be encrypted before sending)
          enableOhipIntegration: data.enableOhipIntegration,
          ohipClientId:
            data.ohipClientId !== undefined
              ? data.ohipClientId
              : original.ohipClientId,
          ohipClientSecret:
            data.ohipClientSecret !== undefined
              ? data.ohipClientSecret
              : original.ohipClientSecret,
          ohipAuthUrl:
            data.ohipAuthUrl !== undefined
              ? data.ohipAuthUrl
              : original.ohipAuthUrl,
          ohipApiBaseUrl:
            data.ohipApiBaseUrl !== undefined
              ? data.ohipApiBaseUrl
              : original.ohipApiBaseUrl,
          ohipHotelId:
            data.ohipHotelId !== undefined
              ? data.ohipHotelId
              : original.ohipHotelId,
          ohipAppKey:
            data.ohipAppKey !== undefined
              ? data.ohipAppKey
              : original.ohipAppKey,
          ohipEnterpriseId:
            data.ohipEnterpriseId !== undefined
              ? data.ohipEnterpriseId
              : original.ohipEnterpriseId,
          ohipExternalSystem:
            data.ohipExternalSystem !== undefined
              ? data.ohipExternalSystem
              : original.ohipExternalSystem,
          ohipScope:
            data.ohipScope !== undefined ? data.ohipScope : original.ohipScope,
          ohipUsername:
            data.ohipUsername !== undefined
              ? data.ohipUsername
              : original.ohipUsername,
          ohipPassword:
            data.ohipPassword !== undefined
              ? data.ohipPassword
              : original.ohipPassword,
          ohipGrantType:
            data.ohipGrantType !== undefined
              ? data.ohipGrantType
              : original.ohipGrantType,
          whatsappToken:
            data.whatsappToken !== undefined
              ? data.whatsappToken
              : original.whatsappToken,

          // Email Integration Credentials
          senderEmail:
            data.senderEmail !== undefined
              ? data.senderEmail
              : original.senderEmail,

          // WhatsApp Integration Credentials
          whatsappApiUrl:
            data.whatsappApiUrl !== undefined
              ? data.whatsappApiUrl
              : original.whatsappApiUrl,
          whatsappPhoneId:
            data.whatsappPhoneId !== undefined
              ? data.whatsappPhoneId
              : original.whatsappPhoneId,
          // _version: original._version,
        };

        // Encrypt credential fields before sending to GraphQL
        const encryptedInput = await encryptCredentialFields(updateInput);

        await client.graphql<Concept>({
          query: updateConcept,
          variables: { input: encryptedInput },
          authMode: 'userPool',
        });
      } else {
        let newReceiveWaiters: any = [];
        if (original!.receiveWaiters) {
          newReceiveWaiters = [...original!.receiveWaiters];
        }
        if (newReceiveWaiters && newReceiveWaiter) {
          newReceiveWaiters.push(newReceiveWaiter);
        }
        if (newReceiveWaiters && delteWaiterID) {
          newReceiveWaiters = newReceiveWaiters.filter((stringWaiter: any) => {
            let objectWaiter = JSON.parse(stringWaiter);
            return objectWaiter.id !== delteWaiterID;
          });
        }

        const updateInput: UpdateConceptInput = {
          id: original.id,
          name: original!.name,
          precedence: original!.precedence,
        };

        await client.graphql<Concept>({
          query: updateConcept,
          variables: { input: updateInput },
          authMode: 'userPool',
        });
      }

      showConfirm(`${singleName} has been updated successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function trash(params: ConceptGetVariables) {
    try {
      const { id, listing } = params;

      const original: any = await get(params);

      if (original) {
        const updateInput: UpdateConceptInput = {
          id: original.id,
          deleted: '1',
          // _version: original._version,
        };

        await client.graphql<Concept>({
          query: updateConcept,
          variables: { input: updateInput },
          authMode: 'userPool',
        });

        dispatch(
          setListing(listing.filter((resource: any) => resource.id !== id)),
        );
      }

      showConfirm(`${singleName} has been moved to trash successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function bulkTrash(params: ConceptBulkTrashVariables) {
    const { ids, listing } = params;

    ids.forEach(async (id: any) => {
      try {
        await trash(id);
      } catch (err: Error | any) {
        throw err;
      }
    });

    dispatch(setListing(listing.filter((model: any) => !ids.has(model.id))));

    showConfirm(`${ids.size} ${listingName} items has been moved to trash`);
  }

  async function remove(params: ConceptGetVariables) {
    const { id, listing } = params;
    try {
      await client.graphql<Concept>({
        query: deleteConcept,
        variables: { id: id },
        authMode: 'userPool',
      });

      dispatch(
        setListing(listing.filter((resource: any) => resource.id !== id)),
      );

      showConfirm(`${singleName} has been deleted successfully`);
    } catch (err: Error | any) {
      showError(err);
    }
  }

  async function exportAll(params: ConceptListingVariables) {
    const data = await fetch(params);
    return data;
  }

  /**
   * Get Resource Name
   *
   * @param id id: string
   *
   * @returns string
   */
  const getName = (params: ConceptGetVariables) => {
    const { id, listing } = params;

    if (listing.length > 0) {
      const model = listing.find((model: Concept) => model.id === id);

      return model ? model.name : '';
    }

    return '';
  };

  function options(listing: Concept[]) {
    const options: Option[] = [];

    for (let option of listing) {
      options.push({ label: option.name, value: option.id });
    }

    return options;
  }

  const headCells: readonly HeadCell[] = [
    {
      id: 'name',
      numeric: false,
      disablePadding: false,
      label: 'Name',
    },
    {
      id: 'precedence',
      numeric: false,
      disablePadding: false,
      label: 'Precedence',
    },
    {
      id: 'createdBy',
      numeric: false,
      disablePadding: false,
      label: 'Created By',
    },
    {
      id: 'createdAt',
      numeric: false,
      disablePadding: false,
      label: 'Date',
    },

    {
      id: 'actions',
      numeric: true,
      disablePadding: false,
      label: '',
    },
  ];

  const dataCells: readonly string[] = ['name', 'precedence'];

  const api: any = {};

  api[`${listingName}CreateSubscription`] = onCreateConcept;

  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}DataCells`] = dataCells;
  api[`${listingName}Options`] = options;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}FetchOnline`] = fetchAllOnline;
  api[`${listingName}Get`] = get;
  api[`${listingName}GetOnline`] = getOnline;
  api[`${listingName}Create`] = create;
  api[`${listingName}Update`] = update;
  api[`${listingName}Trash`] = trash;
  api[`${listingName}BulkTrash`] = bulkTrash;
  api[`${listingName}Delete`] = remove;
  api[`${listingName}Export`] = exportAll;
  api[`${listingName}GetName`] = getName;
  api[`${listingName}ChangeListing`] = (listing: Concept[]) => {
    dispatch(setListing(listing));
    dispatch(setFilters(listing.map((model: any) => model.name)));
  };

  api[`${listingName}ChangeSelected`] = (conceptID: string) => {
    dispatch(setSelected(conceptID));

    localStorage.setItem(LOCAL_STORAGE.SELECTED_CONCEPT, conceptID);
  };
  api[`${listingName}SetBranches`] = (branches: any) =>
    dispatch(setBranches(branches));
  api[`${listingName}ChangeFilters`] = (filters: string[]) =>
    dispatch(setFilters(filters));
  api[`${listingName}ChangeSelectedFilters`] = (filters: any) => {
    dispatch(setSelectedFilters(filters));
  };
  api[`${listingName}NextToken`] = nextToken;
  api[`${listingName}Listing`] = listing;
  api[`${listingName}ClearListing`] = () => dispatch(setListing([]));
  api[`${listingName}ClearNextToken`] = () => dispatch(setNextToken(null));
  api[`${listingName}PrevNextToken`] = undefined;

  return api;
};

export default useResource;
